import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, Subject, Subscription, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';

import { AutoReloadService, ChatEventsService, CountersService, SessionStorageService, UserService } from './index.s';
import { WsAuthResponse, WsMessageModel, WsResponse } from '@shared/models';
import { environment } from 'environments/environment';

const PING_TIMEOUT           = 15000;
const RECONNECT_DELAYS       = [1000, 2000, 5000, 10000, 20000, 30000, 60000, 60000];
const STABLE_CONNECTION_TIME = 30000;
const HEALTH_CHECK_INTERVAL  = 5000;

interface TokenModel {
  iss: string;
  iat: number;
  exp: number;
  aud: string;
  sub: number;
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private readonly WS_TOKEN_API = `${environment.apiUrl}api/portal/v3/web_socket_authentications`;
  private readonly WS_API       = WebsocketService.toWebSocketUrl(`${environment.apiUrl}cable`);

  private messagesSource = new Subject<WsResponse>();
  messages: Observable<WsResponse> = this.messagesSource.asObservable();

  private socket:            WebSocketSubject<WsResponse>;
  private socketSub:         Subscription;
  private connectionId:      number = 0;
  private connecting:        boolean;
  private connected:         boolean;
  private hadConnection:     boolean;
  private stopped:           boolean;
  private fallbackStarted:   boolean;
  private listenersAttached: boolean;

  private reconnectAttempt:  number = 0;
  private reconnectTimer:    ReturnType<typeof setTimeout>;
  private watcherTimer:      ReturnType<typeof setTimeout>;
  private stableTimer:       ReturnType<typeof setTimeout>;
  private healthSub:         Subscription;
  private tokenExpireAt:     Date;

  private pills:             number[] | string;
  private rooms:             Set<number> = new Set();
  private latestMessageUUID: string;

  private readonly visibilityListener = () => this.onVisibilityChange();
  private readonly onlineListener     = () => this.onNetworkOnline();
  constructor(
    private http:                  HttpClient,
    private userService:           UserService,
    private countersService:       CountersService,
    private autoReloadService:     AutoReloadService,
    private chatEventsService:     ChatEventsService,
    private sessionStorageService: SessionStorageService
  ) { }

  private static toWebSocketUrl(url: string): string {
    return new URL(url, window.location.href).href.replace(/^http/, 'ws');
  }

  connect(): void {
    this.stopped = false;
    this.attachListeners();
    this.openConnection();
  }

  closeWebSocket(fallback: boolean = false): void {
    this.stopped = true;
    this.cancelReconnect();
    this.teardownSocket();
    if (fallback) this.startFallback();
  }

  wsOnline(): boolean {
    return !!this.connected;
  }

  reloadWebSocketsChannels(pills: number[] = null): void {
    this.unsubscribe();
    this.unsubscribeFromRooms();
    this.subscribeWebSocketsChannels(pills);
  }

  subscribeToRoom(    roomId: number): void { this.updateRooms('subscribe',   roomId); }
  unsubscribeFromRoom(roomId: number): void { this.updateRooms('unsubscribe', roomId); }

  subscribeToRooms():     void { this.sendCommandToTheRooms('subscribe');   }
  unsubscribeFromRooms(): void { this.sendCommandToTheRooms('unsubscribe'); }

  markMessagesAsRead(roomId: number, messageIds: number[]): void {
    let data = {
      action:     'seen',
      message_ids: messageIds
    };
    this.sendCommandToTheRoom('message', roomId, data);
  }

  startTyping(roomId: number): void {
    let data = { action: 'typing' };
    this.sendCommandToTheRoom('message', roomId, data);
  }

  private openConnection(): void {
    if (this.socket || this.connecting) return;

    const id = ++this.connectionId;
    this.connecting = true;
    this.cancelReconnect();

    this.requestWebSocketToken().pipe(take(1)).subscribe({
      next: token => {
        if (id === this.connectionId) this.openSocket(token);
      },
      error: err => {
        if (id !== this.connectionId) return;
        this.connecting = false;
        console.warn('[ws] token request failed', err);
        this.onConnectionLost();
      }
    });
  }

  private requestWebSocketToken(): Observable<string> {
    return this.http.post<WsAuthResponse>(this.WS_TOKEN_API, {}).pipe(map(data => data.websocket_jwt));
  }

  private openSocket(token: string): void {
    this.saveTokenExpireTime(token);

    const socket = new WebSocketSubject<WsResponse>({ url: `${this.WS_API}?jid=${token}` });
    this.socket    = socket;
    this.socketSub = socket.subscribe({
      next:     message => this.handleMessage(message),
      error:    err     => this.onSocketClosed(socket, err),
      complete: ()      => this.onSocketClosed(socket)
    });

    this.resetWatcher();
  }

  private handleMessage(message: WsResponse): void {
    try {
      switch (message?.type) {
        case 'ping':                 this.resetWatcher(); return;
        case 'welcome':              this.onWelcome();     return;
        case 'confirm_subscription':                       return;
        case 'reject_subscription':  console.warn('[ws] subscription rejected', message.identifier);                              return;
        case 'disconnect':           console.warn('[ws] server disconnect', message.reason, message.reconnect ? '(reconnect)' : ''); return;
      }

      this.messagesSource.next(message);
      this.dispatch(message);
    } catch (err) {
      console.error('[ws] failed to handle message', err, message);
    }
  }

  private onWelcome(): void {
    this.connecting = false;
    this.connected  = true;
    this.resetWatcher();
    this.startStableTimer();

    this.subscribeWebSocketsChannels();
    if (this.userService.isInternal) {
      this.sendCommand('subscribe', 'InternalNotificationsChannel');
      this.chatEventsService.init();
    }

    if (this.hadConnection) this.autoReloadService.forceAutoReload();
    this.hadConnection = true;
  }

  private dispatch(message: WsResponse): void {
    let channel   = message?.identifier ? JSON.parse(message.identifier).channel : null;
    let wsMessage = message?.message as WsMessageModel;

    if (channel === this.getChannelName() && wsMessage?.id && (!this.latestMessageUUID || this.latestMessageUUID !== wsMessage.event_uuid)) {
      this.latestMessageUUID = wsMessage.event_uuid;
      this.countersService.resourceStateTransitionHandler(wsMessage);
    }
    if (channel === 'InternalNotificationsChannel' && wsMessage?.event === 'location_changes') this.userService.requestUserData().subscribe({
      next:  user => this.userService.setPills(user, true),
      error: err  => console.error(err)
    });
  }

  private onSocketClosed(socket: WebSocketSubject<WsResponse>, err: any = null): void {
    if (socket !== this.socket) return;

    if (err instanceof CloseEvent) console.warn(`[ws] connection closed (code ${err.code})`);
    else if (err)                  console.warn('[ws] connection error', err);
    else                           console.warn('[ws] connection closed by server');

    this.teardownSocket();
    this.onConnectionLost();
  }

  private teardownSocket(): void {
    this.connectionId++;
    this.connecting = false;
    this.connected  = false;
    this.clearWatcher();
    this.clearStableTimer();

    const sub = this.socketSub;
    this.socketSub = null;
    this.socket    = null;
    sub?.unsubscribe();
  }

  private onConnectionLost(): void {
    if (this.stopped) return;
    if (document.visibilityState === 'hidden') return;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.socket || this.connecting || this.reconnectTimer || this.healthSub) return;

    const exhausted = this.reconnectAttempt >= RECONNECT_DELAYS.length;
    const delay     = this.withJitter(RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)]);
    this.reconnectAttempt++;
    if (exhausted) this.startFallback();

    console.info(`[ws] reconnect attempt ${this.reconnectAttempt} in ${Math.round(delay / 1000)}s`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.stopped || document.visibilityState === 'hidden') return;
      this.waitForApiThenConnect();
    }, delay);
  }

  private waitForApiThenConnect(): void {
    if (this.socket || this.connecting || this.healthSub) return;

    this.healthSub = merge(timer(0, HEALTH_CHECK_INTERVAL), fromEvent(window, 'online')).pipe(
      switchMap(() => this.healthCheck()),
      filter(healthy => healthy),
      take(1)
    ).subscribe(() => {
      this.healthSub = null;
      if (document.visibilityState === 'hidden') return;
      this.openConnection();
    });
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.healthSub) {
      this.healthSub.unsubscribe();
      this.healthSub = null;
    }
  }

  private healthCheck(): Observable<boolean> {
    return this.http.get(`${environment.apiUrl}healthz`, { responseType: 'text' }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private startFallback(): void {
    if (this.fallbackStarted) return;
    this.fallbackStarted = true;
    console.warn('[ws] falling back to timer based reloads');
    this.autoReloadService.fallbackToTimerReload().subscribe();
  }

  private withJitter(ms: number): number {
    return Math.round(ms * (0.8 + Math.random() * 0.4));
  }

  private resetWatcher(): void {
    this.clearWatcher();
    this.watcherTimer = setTimeout(() => {
      this.watcherTimer = null;
      if (!this.socket) return;
      console.warn(`[ws] no ping for ${PING_TIMEOUT / 1000}s, reconnecting`);
      this.teardownSocket();
      this.onConnectionLost();
    }, PING_TIMEOUT);
  }

  private clearWatcher(): void {
    if (!this.watcherTimer) return;
    clearTimeout(this.watcherTimer);
    this.watcherTimer = null;
  }

  private startStableTimer(): void {
    this.clearStableTimer();
    this.stableTimer = setTimeout(() => {
      this.stableTimer      = null;
      this.reconnectAttempt = 0;
    }, STABLE_CONNECTION_TIME);
  }

  private clearStableTimer(): void {
    if (!this.stableTimer) return;
    clearTimeout(this.stableTimer);
    this.stableTimer = null;
  }

  private attachListeners(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;
    document.addEventListener('visibilitychange', this.visibilityListener);
    window.addEventListener('online', this.onlineListener);
  }

  private onVisibilityChange(): void {
    if (document.visibilityState !== 'visible' || this.stopped) return;

    if (this.socket && this.wsTokenExpired()) {
      console.info('[ws] token expired while in background, reconnecting');
      this.teardownSocket();
    }
    if (!this.socket && !this.connecting && !this.reconnectTimer && !this.healthSub) this.waitForApiThenConnect();
  }

  private onNetworkOnline(): void {
    if (this.stopped || this.socket || this.connecting || this.healthSub) return;
    if (document.visibilityState === 'hidden') return;
    this.cancelReconnect();
    this.waitForApiThenConnect();
  }

  private saveTokenExpireTime(token: string): void {
    let exp = this.parseJWT(token)?.exp;
    this.tokenExpireAt = exp ? new Date(exp * 1000) : null;
  }

  private wsTokenExpired(): boolean {
    return !!this.tokenExpireAt && this.tokenExpireAt.getTime() <= Date.now();
  }

  private parseJWT(token: string): TokenModel {
    try {
      let base64      = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      let jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  private subscribeWebSocketsChannels(pills: number[] = null): void {
    if (pills?.length) this.pills = pills;
    else {
      let activePills = this.sessionStorageService.activePills;
      if (this.userService.isInternal) this.pills = activePills.length ? activePills.map(p => p.id) : 'all';
      if (this.userService.isCustomer) this.pills = activePills.length ? activePills.map(p => p.id) : null;
    }
    this.subscribe();
    this.subscribeToRooms();
  }

  private subscribe(): void {
    this.sendCommand('subscribe', this.getChannelName(), 'ids', this.pills);
  }

  private unsubscribe(): void {
    if (this.pills) this.sendCommand('unsubscribe', this.getChannelName(), 'ids', this.pills);
    this.pills = null;
  }

  private getChannelName(): string {
    if (this.userService.isInternal) return 'InternalLocationsChannel';
    if (this.userService.isCustomer) return 'CustomerCompaniesChannel';
  }

  private sendCommand(command: string, channel: string, filterName?: string, filterValue?: any, extra?: any): void {
    let filter     = filterName ? { [filterName]: filterValue   } : null;
    let identifier = JSON.stringify(Object.assign({}, { channel }, filter));

    let options: any = { command, identifier };
    if (extra) options.data = JSON.stringify(extra);

    if (this.socket) this.socket.next(<any>options);
    else console.warn(`[ws] not connected, dropped "${command}" for ${identifier}`);
  }

  private updateRooms(action: string, roomId: number): void {
    if (!isNaN(roomId as number)) {
      let roomsLength = this.rooms.size;
      if (action === 'subscribe'   && !this.rooms.has(roomId)) this.rooms.add(   +roomId);
      if (action === 'unsubscribe' &&  this.rooms.has(roomId)) this.rooms.delete(+roomId);
      if (roomsLength !== this.rooms.size) this.sendCommandToTheRoom(action, roomId);
    }
  }

  private sendCommandToTheRooms(command: string): void {
    if (this.rooms.size) this.rooms.forEach(r => this.sendCommandToTheRoom(command, r));
  }

  private sendCommandToTheRoom(command: string, roomId: number, data: any = null): void {
    this.sendCommand(command, 'Chat::RoomsChannel', 'room_id', roomId, data);
  }

}
